import { type FC, type ReactNode } from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';

import { FormattedMessage } from 'dibs-react-intl';
import { Button } from 'dibs-elements/exports/Button.tsx';
import { Link } from 'dibs-elements/exports/Link.tsx';

import { CUSTOMER_SUPPORT_URL, TRADE_SALES_EMAIL } from '../../tradeBenefitsConstants.ts';
import { useTradeBenefitsContact } from '../../useTradeBenefitsContact.ts';
import { trackTradeBenefitsEvent } from '../../tradeBenefitsTracking.ts';

import { useServerVars } from '../../../shared/ServerVarsContext.tsx';

import { type TradeBenefitsHeroStatusContact_viewer$key } from './__generated__/TradeBenefitsHeroStatusContact_viewer.graphql.ts';
import { type TradeBenefitsHeroStatusContact_tradeFirm$key } from './__generated__/TradeBenefitsHeroStatusContact_tradeFirm.graphql.ts';

import styles from './TradeBenefitsHeroStatusContact.scss';
import dibsCss from 'dibs-css';

type ContactContent = {
    title: ReactNode;
    description: ReactNode;
    buttonLabel: ReactNode;
    href: string;
};

const getContactContent = ({
    isSilverPlus,
    isAssigned,
    displayName,
    firstName,
    repEmail,
}: {
    isSilverPlus: boolean;
    isAssigned: boolean;
    displayName: string | null | undefined;
    firstName: string | null | undefined;
    repEmail: string | null | undefined;
}): ContactContent => {
    if (isSilverPlus && isAssigned) {
        return {
            title: (
                <FormattedMessage
                    id="abt.TradeBenefitsHeroStatusContact.assigned.title"
                    defaultMessage="Have questions or need help with a project?"
                />
            ),
            description: (
                <FormattedMessage
                    id="abt.TradeBenefitsHeroStatusContact.assigned.description"
                    defaultMessage="Your Dedicated Account Representative,{break} {representative}, is here to help."
                    values={{
                        representative: (
                            <span className={dibsCss.sassyFontBodySmallHeavy}>{displayName}</span>
                        ),
                        break: <br className={styles.mobileBreak} />,
                    }}
                />
            ),
            buttonLabel: (
                <FormattedMessage
                    id="abt.TradeBenefitsHeroStatusContact.assigned.button"
                    defaultMessage="Contact {firstName}"
                    values={{ firstName }}
                />
            ),
            href: `mailto:${repEmail}`,
        };
    }

    if (isSilverPlus) {
        return {
            title: (
                <FormattedMessage
                    id="abt.TradeBenefitsHeroStatusContact.unassigned.title"
                    defaultMessage="Have questions or need help with a project?"
                />
            ),
            description: (
                <FormattedMessage
                    id="abt.TradeBenefitsHeroStatusContact.unassigned.description"
                    defaultMessage="Our Dedicated Account Representatives are here to help."
                />
            ),
            buttonLabel: (
                <FormattedMessage
                    id="abt.TradeBenefitsHeroStatusContact.unassigned.button"
                    defaultMessage="Contact Trade Sales"
                />
            ),
            href: `mailto:${TRADE_SALES_EMAIL}`,
        };
    }

    return {
        title: (
            <FormattedMessage
                id="abt.TradeBenefitsHeroStatusContact.support.title"
                defaultMessage="Need help with an order?"
            />
        ),
        description: (
            <FormattedMessage
                id="abt.TradeBenefitsHeroStatusContact.support.description"
                defaultMessage="Our Customer Support team is here to assist."
            />
        ),
        buttonLabel: (
            <FormattedMessage
                id="abt.TradeBenefitsHeroStatusContact.support.button"
                defaultMessage="Contact Support"
            />
        ),
        href: CUSTOMER_SUPPORT_URL,
    };
};

const viewerFragment = graphql`
    fragment TradeBenefitsHeroStatusContact_viewer on Viewer {
        ...useTradeBenefitsContact_viewer
    }
`;

const tradeFirmFragment = graphql`
    fragment TradeBenefitsHeroStatusContact_tradeFirm on TradeFirmType {
        ...useTradeBenefitsContact_tradeFirm
    }
`;

const TradeBenefitsHeroStatusContact: FC<{
    viewer: TradeBenefitsHeroStatusContact_viewer$key;
    tradeFirm: TradeBenefitsHeroStatusContact_tradeFirm$key | null | undefined;
}> = ({ viewer: viewerRef, tradeFirm: tradeFirmRef }) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const tradeFirm = useFragment(tradeFirmFragment, tradeFirmRef);
    const { isMobile } = useServerVars();
    const { isSilverPlus, isAssigned, displayName, firstName, repEmail } = useTradeBenefitsContact({
        viewer,
        tradeFirm,
    });

    const { title, description, buttonLabel, href } = getContactContent({
        isSilverPlus,
        isAssigned,
        displayName,
        firstName,
        repEmail,
    });

    const trackContactClick = (): void => {
        trackTradeBenefitsEvent({
            stepInteractionName: 'contact support clicked',
            trigger: 'dashboard header',
        });
    };

    return (
        <div
            className={classnames(
                styles.container,
                dibsCss.wFull,
                dibsCss.flex,
                dibsCss.justifyBetween,
                dibsCss.gapSmall
            )}
        >
            <div>
                <div className={classnames(styles.title, dibsCss.sassyFontBodyLargeHeavy)}>
                    {title}
                </div>
                <div className={classnames(styles.description, dibsCss.sassyFontBodySmall)}>
                    {description}
                </div>
            </div>
            {!isMobile && (
                <Button
                    href={href}
                    style="circle"
                    type="standard"
                    target="_blank"
                    onClick={trackContactClick}
                >
                    {buttonLabel}
                </Button>
            )}
            {isMobile && (
                <Link href={href} target="_blank" onClick={trackContactClick}>
                    <span className={dibsCss.sassyFontBody}>
                        <FormattedMessage
                            id="abt.TradeBenefitsHeroStatusContact.contactNow"
                            defaultMessage="Contact Now"
                        />
                    </span>
                </Link>
            )}
        </div>
    );
};

export { TradeBenefitsHeroStatusContact };
